<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import type { ApiClient } from '@xjlv2/api-client'
import {
  AdminFilterBar,
  AdminDataTable,
  AdminStatusTag,
  AdminDrawer,
  RrInput,
  RrSelect,
  RrButton,
  RrForm,
  RrFormItem,
  RrCheckbox,
  RrTag,
  type TableColumnType
} from '@xjlv2/admin-layout'
import type { CreateUserRequest, Dept, PageResult, Role, TenantMenu, User } from '../../types'

interface UserWithMeta extends User {
  deptName: string
  roleNames: string[]
  menus: TenantMenu[]
}

const props = defineProps<{
  createApiClient: () => ApiClient
  roles: Role[]
  depts: Dept[]
  menus: TenantMenu[]
  canCreate: boolean
}>()

const showCreateForm = ref(false)
const rawUsers = ref<User[]>([])
const total = ref(0)
const page = ref(1)
const pageSize = ref(20)
const loading = ref(false)

const filters = ref({
  keyword: '',
  deptId: 0,
  roleId: 0,
  status: 'ALL' as 'ALL' | 'ENABLED' | 'DISABLED'
})
const form = ref({
  username: 'operator',
  displayName: '运营人员',
  deptId: 0,
  roleIds: [] as number[]
})

const defaultDeptId = computed(() => props.depts[0]?.id ?? 0)

const usersWithMeta = computed<UserWithMeta[]>(() =>
  rawUsers.value.map((user) => {
    const userRoleIds = new Set(user.roleIds)
    const userMenuCodes = new Set(
      props.roles
        .filter((role) => userRoleIds.has(role.id))
        .flatMap((role) => role.menuCodes)
    )
    return {
      ...user,
      deptName: props.depts.find((dept) => dept.id === user.deptId)?.name ?? `部门 ${user.deptId}`,
      roleNames: user.roleIds.map((roleId) => roleName(roleId)),
      menus: props.menus.filter((menu) => menu.enabled && userMenuCodes.has(menu.menuCode))
    }
  })
)

async function fetchUsers() {
  loading.value = true
  try {
    const api = props.createApiClient()
    const params = new URLSearchParams({
      pageNo: String(page.value),
      pageSize: String(pageSize.value)
    })
    if (filters.value.keyword.trim()) params.set('keyword', filters.value.keyword.trim())
    if (filters.value.deptId) params.set('deptId', String(filters.value.deptId))
    if (filters.value.roleId) params.set('roleId', String(filters.value.roleId))
    if (filters.value.status !== 'ALL') params.set('status', filters.value.status)
    const res = await api.get<PageResult<User>>(`/api/saas/system/rbac/users?${params.toString()}`)
    rawUsers.value = res.records
    total.value = res.total
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  void fetchUsers()
})

watch([page, pageSize], () => {
  void fetchUsers()
})

watch(
  [() => filters.value.keyword, () => filters.value.deptId, () => filters.value.roleId, () => filters.value.status],
  () => {
    page.value = 1
    void fetchUsers()
  }
)

watch(
  () => props.depts,
  (depts) => {
    if (depts.length === 0) return
    const hasSelectedDept = depts.some((dept) => dept.id === form.value.deptId)
    if (!hasSelectedDept) form.value.deptId = depts[0].id
  },
  { immediate: true }
)

const columns: TableColumnType<UserWithMeta>[] = [
  { key: 'user', title: '用户', minWidth: 160 },
  { key: 'dept', title: '部门', minWidth: 140 },
  { key: 'roles', title: '角色', minWidth: 160 },
  { key: 'menus', title: '继承菜单', minWidth: 180 },
  { key: 'status', title: '状态', width: 90 }
]

function toggleRole(roleId: number) {
  const selected = new Set(form.value.roleIds)
  if (selected.has(roleId)) selected.delete(roleId)
  else selected.add(roleId)
  form.value.roleIds = Array.from(selected).sort((left, right) => left - right)
}

function roleName(roleId: number) {
  return props.roles.find((role) => role.id === roleId)?.name ?? `角色 ${roleId}`
}

function resetFilters() {
  filters.value = {
    keyword: '',
    deptId: 0,
    roleId: 0,
    status: 'ALL'
  }
}

async function createUser() {
  if (!props.canCreate || !form.value.username || !form.value.displayName) return
  loading.value = true
  try {
    const api = props.createApiClient()
    await api.post<User>('/api/saas/system/rbac/users', {
      username: form.value.username,
      displayName: form.value.displayName,
      deptId: form.value.deptId || defaultDeptId.value,
      roleIds: form.value.roleIds
    })
    showCreateForm.value = false
    form.value.username = `${form.value.username}-next`
    await fetchUsers()
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <section class="panel span management-list">
    <div class="panel-head">
      <div>
        <p class="eyebrow">User Management</p>
        <h2>管理列表</h2>
      </div>
      <span>共 {{ total }} 个用户</span>
    </div>

    <AdminFilterBar :loading="loading" @reset="resetFilters">
      <RrInput v-model:value.trim="filters.keyword" autocomplete="off" placeholder="账号、姓名、部门、角色" />
      <RrSelect
        v-model:value.number="filters.deptId"
        :options="[{ label: '全部部门', value: 0 }, ...depts.map(d => ({ label: d.name, value: d.id }))]"
      />
      <RrSelect
        v-model:value.number="filters.roleId"
        :options="[{ label: '全部角色', value: 0 }, ...roles.map(r => ({ label: r.name, value: r.id }))]"
      />
      <RrSelect
        v-model:value="filters.status"
        :options="[
          { label: '全部状态', value: 'ALL' },
          { label: '启用', value: 'ENABLED' },
          { label: '停用', value: 'DISABLED' }
        ]"
      />
      <template #actions>
        <RrButton type="default" :disabled="loading" @click="resetFilters">重置</RrButton>
        <RrButton type="primary" :disabled="!canCreate || loading" @click="showCreateForm = !showCreateForm">
          {{ showCreateForm ? '收起新增' : '新增用户' }}
        </RrButton>
      </template>
    </AdminFilterBar>

    <AdminDrawer
      v-model:open="showCreateForm"
      title="新增用户"
      :description="canCreate ? '可写' : '只读'"
    >
      <RrForm @submit.prevent="createUser">
        <fieldset :disabled="!canCreate || loading" class="form-grid inline-form-grid">
          <RrFormItem label="账号">
            <RrInput v-model:value="form.username" />
          </RrFormItem>
          <RrFormItem label="姓名">
            <RrInput v-model:value="form.displayName" />
          </RrFormItem>
          <RrFormItem label="部门">
            <RrSelect
              v-model:value.number="form.deptId"
              :options="depts.map(d => ({ label: d.name, value: d.id }))"
            />
          </RrFormItem>
        </fieldset>

        <div class="check-grid compact-check-grid" style="margin-top: 16px;">
          <label v-for="role in roles" :key="role.id" class="check-row">
            <RrCheckbox
              :checked="form.roleIds.includes(role.id)"
              :disabled="!canCreate || loading"
              @change="toggleRole(role.id)"
            />
            <span>{{ role.name }}</span>
          </label>
          <p v-if="roles.length === 0" class="empty-state">暂无可分配角色。</p>
        </div>

        <template #footer>
          <RrButton type="primary" :disabled="!canCreate || loading" @click="createUser">
            创建用户
          </RrButton>
        </template>
      </RrForm>
    </AdminDrawer>

    <AdminDataTable
      class="user-management-table"
      :columns="columns"
      :data-source="usersWithMeta"
      :loading="loading"
      :current="page"
      :page-size="pageSize"
      :total="total"
      row-key="id"
      :count="usersWithMeta.length"
      :empty="total === 0"
      empty-title="暂无匹配用户"
      @update:current="page = $event"
      @update:page-size="pageSize = $event"
    >
      <template #bodyCell="{ column, record }">
        <template v-if="column.key === 'user'">
          <div>
            <strong>{{ record.displayName }}</strong>
            <small style="display: block; color: var(--rr-color-text-muted);">{{ record.username }} · ID {{ record.id }}</small>
          </div>
        </template>
        <template v-else-if="column.key === 'dept'">
          <div>
            <strong>{{ record.deptName }}</strong>
            <small style="display: block; color: var(--rr-color-text-muted);">部门 ID {{ record.deptId }}</small>
          </div>
        </template>
        <template v-else-if="column.key === 'roles'">
          <div style="display: flex; gap: 4px; flex-wrap: wrap;">
            <RrTag v-for="role in record.roleNames" :key="role">{{ role }}</RrTag>
            <span v-if="record.roleNames.length === 0" style="color: var(--rr-color-text-muted); font-size: 12px;">未分配</span>
          </div>
        </template>
        <template v-else-if="column.key === 'menus'">
          <div style="display: flex; gap: 4px; flex-wrap: wrap;">
            <RrTag v-for="menu in record.menus.slice(0, 4)" :key="menu.menuCode">{{ menu.name }}</RrTag>
            <RrTag v-if="record.menus.length > 4" tone="neutral">+{{ record.menus.length - 4 }}</RrTag>
            <span v-if="record.menus.length === 0" style="color: var(--rr-color-text-muted); font-size: 12px;">无菜单</span>
          </div>
        </template>
        <template v-else-if="column.key === 'status'">
          <AdminStatusTag :tone="record.enabled ? 'success' : 'neutral'">
            {{ record.enabled ? '启用' : '停用' }}
          </AdminStatusTag>
        </template>
      </template>
    </AdminDataTable>
  </section>
</template>
